<template>
  <div id="downloads">
    <h3 class="title">
      Downloads
    </h3>
    <h5 class="subtitle">
      Download or watch episodes and shorts.
    </h5>

    <transition
      name="fadeUp"
      :duration="500"
    >
      <h4
        v-if="!$parent.downloads.ready"
        class="title has-text-centered"
      >
        Loading...
      </h4>
    </transition>

    <transition
      name="fadeLeft"
      :duration="1500"
    >
      <div
        v-if="$parent.downloads.restricted"
        class="notification is-danger"
      >
        <strong>Sorry, but we can't show you this page.</strong><br>
        It looks like you're in a country where the show is freely available from legal sources.<br>
        Please use those sources instead of pirating the show. Thanks!
      </div>
    </transition>

    <transition
      name="fadeLeft"
      :duration="1500"
    >
      <div
        v-if="$parent.downloads.ext_blocked"
        class="notification is-danger"
      >
        <strong>An external ressource couldn't be loaded.</strong><br>
        Sorry, but we couldn't load an external ressource needed to verify your connexion to our downloads.<br>
        Please disable your adblocker for this website (promise: we don't have ads)
        and/or allow <code>https://okko-region.herokuapp.com</code> to load.
      </div>
    </transition>

    <transition
      name="fadeUp"
      :duration="1500"
    >
      <b-tabs
        v-if="mnt && $parent.downloads.ep.length > 0"
        position="is-centered"
        class="block"
      >
        <b-tab-item
          v-if="$parent.downloads.ep.length > 0"
          label="Episodes"
          icon="television"
        >
          <div
            v-if="$parent.downloads.ep_legal !== null"
            class="box legal-links"
          >
            Please consider official and legal links if available.<br>
            <a
              v-for="t in $parent.downloads.ep_legal"
              :key="t.url"
              class="button"
              :href="t.url"
            >
              <b-icon :icon="t.icon" />
              <span>{{ t.title }}</span>
            </a>
          </div>

          <div class="dl-entries columns is-multiline is-mobile is-centered">
            <div
              v-for="l in $parent.downloads.ep"
              :key="l.code"
              class="dl-entry column is-10"
            >
              <span class="dl-title">
                S{{ l.season.toString() | leftPad(2, '0') }}E{{ l.episode.toString() | leftPad(2, '0') }}:
                <strong>{{ l.title }}</strong>
              </span>

              <span class="dl-links">
                <b-tooltip
                  v-if="l.direct"
                  label="Direct"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.direct"
                  >
                    <b-icon icon="download" />
                  </a>
                </b-tooltip>
                <b-tooltip
                  v-if="l.torrent_file"
                  label=".torrent"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.torrent_file"
                  >
                    <b-icon icon="connectdevelop" />
                  </a>
                </b-tooltip>
                <b-tooltip
                  v-if="l.magnet"
                  label="Magnet"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.magnet"
                  >
                    <b-icon icon="magnet" />
                  </a>
                </b-tooltip>
                <b-tooltip
                  v-if="l.mega"
                  label="Mega"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.mega"
                  >
                    <b-icon icon="cloud-download" />
                  </a>
                </b-tooltip>
                <b-tooltip
                  v-if="l.dailymotion"
                  label="Dailymotion"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.dailymotion"
                  >
                    <b-icon icon="video-camera" />
                  </a>
                </b-tooltip>
                <b-tooltip
                  v-if="l.other"
                  label="Other"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.other"
                  >
                    <b-icon icon="external-link" />
                  </a>
                </b-tooltip>
              </span>
            </div>
          </div>
        </b-tab-item>

        <b-tab-item
          v-if="$parent.downloads.sh.length > 0"
          label="Shorts"
          icon="video-camera"
        >
          <div
            v-if="$parent.downloads.sh_legal !== null"
            class="box legal-links"
          >
            Please consider official and legal links if available.<br>
            <a
              v-for="t in $parent.downloads.sh_legal"
              :key="t.url"
              class="button"
              :href="t.url"
            >
              <b-icon :icon="t.icon" />
              <span>{{ t.title }}</span>
            </a>
          </div>

          <div class="dl-entries columns is-multiline">
            <div
              v-for="l in $parent.downloads.sh"
              :key="l.code"
              class="dl-entry column is-10 is-offset-1"
            >
              <span class="dl-title">
                <strong>{{ l.title }}</strong>
              </span>

              <span class="dl-links">
                <b-tooltip
                  v-if="l.direct"
                  label="Direct"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.direct"
                  >
                    <b-icon icon="download" />
                  </a>
                </b-tooltip>
                <b-tooltip
                  v-if="l.torrent_file"
                  label=".torrent"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.torrent_file"
                  >
                    <b-icon icon="connectdevelop" />
                  </a>
                </b-tooltip>
                <b-tooltip
                  v-if="l.magnet"
                  label="Magnet"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.magnet"
                  >
                    <b-icon icon="magnet" />
                  </a>
                </b-tooltip>
                <b-tooltip
                  v-if="l.mega"
                  label="Mega"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.mega"
                  >
                    <b-icon icon="cloud-download" />
                  </a>
                </b-tooltip>
                <b-tooltip
                  v-if="l.dailymotion"
                  label="Dailymotion"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.dailymotion"
                  >
                    <b-icon icon="video-camera" />
                  </a>
                </b-tooltip>
                <b-tooltip
                  v-if="l.other"
                  label="Other"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.other"
                  >
                    <b-icon icon="external-link" />
                  </a>
                </b-tooltip>
              </span>
            </div>
          </div>
        </b-tab-item>

        <b-tab-item
          v-if="$parent.downloads.co.length > 0"
          label="Comics"
          icon="book"
        >
          <div
            v-if="$parent.downloads.co_legal !== null"
            class="box legal-links"
          >
            Please consider official and legal links if available.<br>
            <a
              v-for="t in $parent.downloads.co_legal"
              :key="t.url"
              class="button"
              :href="t.url"
            >
              <b-icon :icon="t.icon" />
              <span>{{ t.title }}</span>
            </a>
          </div>

          <div class="dl-entries columns is-multiline">
            <div
              v-for="l in $parent.downloads.co"
              :key="l.code"
              class="dl-entry column is-10 is-offset-1"
            >
              <span class="dl-title">
                <strong>{{ l.title }}</strong>
              </span>

              <span class="dl-links">
                <b-tooltip
                  v-if="l.direct"
                  label="Direct"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.direct"
                  >
                    <b-icon icon="download" />
                  </a>
                </b-tooltip>
                <b-tooltip
                  v-if="l.torrent_file"
                  label=".torrent"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.torrent_file"
                  >
                    <b-icon icon="connectdevelop" />
                  </a>
                </b-tooltip>
                <b-tooltip
                  v-if="l.magnet"
                  label="Magnet"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.magnet"
                  >
                    <b-icon icon="magnet" />
                  </a>
                </b-tooltip>
                <b-tooltip
                  v-if="l.mega"
                  label="Mega"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.mega"
                  >
                    <b-icon icon="cloud-download" />
                  </a>
                </b-tooltip>
                <b-tooltip
                  v-if="l.other"
                  label="Other"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.other"
                  >
                    <b-icon icon="external-link" />
                  </a>
                </b-tooltip>
              </span>
            </div>
          </div>
        </b-tab-item>

        <b-tab-item
          v-if="$parent.downloads.mu.length > 0"
          label="Soundtrack"
          icon="music"
        >
          <div
            v-if="$parent.downloads.mu_legal !== null"
            class="box legal-links"
          >
            Please consider official and legal links if available.<br>
            <a
              v-for="t in $parent.downloads.mu_legal"
              :key="t.url"
              class="button"
              :href="t.url"
            >
              <b-icon :icon="t.icon" />
              <span>{{ t.title }}</span>
            </a>
          </div>

          <div class="dl-entries columns is-multiline">
            <div
              v-for="l in $parent.downloads.mu"
              :key="l.code"
              class="dl-entry column is-10 is-offset-1"
            >
              <span class="dl-title">
                <strong>{{ l.title }}</strong>
              </span>

              <span class="dl-links">
                <b-tooltip
                  v-if="l.direct"
                  label="Direct"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.direct"
                  >
                    <b-icon icon="download" />
                  </a>
                </b-tooltip>
                <b-tooltip
                  v-if="l.torrent_file"
                  label=".torrent"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.torrent_file"
                  >
                    <b-icon icon="connectdevelop" />
                  </a>
                </b-tooltip>
                <b-tooltip
                  v-if="l.magnet"
                  label="Magnet"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.magnet"
                  >
                    <b-icon icon="magnet" />
                  </a>
                </b-tooltip>
                <b-tooltip
                  v-if="l.mega"
                  label="Mega"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.mega"
                  >
                    <b-icon icon="cloud-download" />
                  </a>
                </b-tooltip>
                <b-tooltip
                  v-if="l.other"
                  label="Other"
                >
                  <a
                    class="button is-small is-dark"
                    :href="l.other"
                  >
                    <b-icon icon="external-link" />
                  </a>
                </b-tooltip>
              </span>
            </div>
          </div>
        </b-tab-item>
      </b-tabs>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'Downloads',
  metaInfo: {
    title: 'Downloads'
  },
  data () {
    return {
      mnt: false
    }
  },
  mounted () {
    var t = this
    setTimeout(function () {
      t.mnt = true
    }, t.$parent.mntDelay)
  }
}
</script>

<style lang="scss">
.tabs {
  li:not(.is-active) span:not(.icon) {
    display: none !important;
  }
}

.legal-links {
  margin: 1.5rem 1.5rem 0 !important;
  text-align: center;
}

.dl-entries {
  margin-top: 1.5rem !important;

  .dl-entry {
    display: flex;
    flex-direction: row;

    &:nth-child(even) {
      background-color: hsl(0, 0%, 96%);
    }

    .dl-title {
      flex: auto;
    }

    .dl-links {
      flex: none;
      max-width: 220px;
    }
  }
}
</style>
